// Sentry scrubbing — strips query strings and fragments from every URL
// an event, transaction or breadcrumb carries before it leaves the
// process. `sendDefaultPii: false` does not cover URLs, and ours carry
// secrets in the query: OAuth callbacks (`code`, `state`,
// `installation_id`), admin-consent returns, `/signin?next=<callback>`,
// magic-link / invite tokens, API calls with tokens in the query.
// The pathname is kept — it is what makes an event debuggable.
//
// Pure + dependency-free (no @sentry import) so it is unit-testable
// and the API keeps an identical copy (apps/api/src/lib/sentryScrub.ts).
// Keep the two in step.

type Dict = Record<string, unknown>;

function isDict(v: unknown): v is Dict {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Drop `?query` and `#fragment` from a URL or path, keep the rest.
 *  Works on absolute URLs, relative paths and bare `?a=b` strings. */
export function stripUrlQuery(url: string): string {
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

// A URL-ish token — `scheme://…` or a `/path` — immediately followed by
// a non-empty `?…` or `#…`. Only the query/fragment is removed. Prose
// such as "and/or? yes" is untouched because the `?` must be followed
// by a non-space character.
const URL_WITH_QUERY = /((?:[a-z][a-z0-9+.-]*:\/\/|\/)[^\s?#"'<>`]*)[?#][^\s"'<>`]+/gi;

/** Strip query strings / fragments from anything URL-shaped inside free
 *  text (exception messages, breadcrumb messages, span descriptions). */
export function scrubUrlsInText(text: string): string {
  return text.replace(URL_WITH_QUERY, '$1');
}

// Headers that carry credentials or session state. Matched
// case-insensitively against the header name.
const SECRET_HEADER = /authorization|cookie|token|secret|api[-_]?key|signature|session/i;
// Headers whose value is a URL — kept, minus the query.
const URL_HEADERS = new Set(['referer', 'referrer', 'origin', 'location']);

// Breadcrumb / span `data` keys that hold a URL.
const URL_DATA_KEYS = ['url', 'from', 'to', 'http.url', 'url.full', 'http.target'];
// Span `data` keys that hold only the query / fragment.
const QUERY_DATA_KEYS = ['http.query', 'http.fragment', 'url.query', 'url.fragment'];

function scrubData(data: unknown): void {
  if (!isDict(data)) return;
  for (const k of URL_DATA_KEYS) {
    if (typeof data[k] === 'string') data[k] = stripUrlQuery(data[k] as string);
  }
  for (const k of QUERY_DATA_KEYS) delete data[k];
}

/** Scrub a breadcrumb in place (navigation `from`/`to`, fetch/xhr/http
 *  `url`, message text). Returns it for use as `beforeBreadcrumb`. */
export function scrubBreadcrumb<T>(breadcrumb: T): T {
  const b: unknown = breadcrumb;
  if (!isDict(b)) return breadcrumb;
  if (typeof b.message === 'string') b.message = scrubUrlsInText(b.message);
  scrubData(b.data);
  return breadcrumb;
}

function scrubRequest(request: unknown): void {
  if (!isDict(request)) return;
  if (typeof request.url === 'string') request.url = stripUrlQuery(request.url);
  delete request.query_string;
  delete request.cookies;
  delete request.data;
  if (isDict(request.headers)) {
    const headers = request.headers;
    for (const name of Object.keys(headers)) {
      if (SECRET_HEADER.test(name)) {
        delete headers[name];
      } else if (URL_HEADERS.has(name.toLowerCase()) && typeof headers[name] === 'string') {
        headers[name] = stripUrlQuery(headers[name] as string);
      }
    }
  }
}

/** Scrub an error event or transaction in place. Returns it for use as
 *  `beforeSend` / `beforeSendTransaction`. User, tags, release and
 *  environment are left alone. */
export function scrubEvent<T>(input: T): T {
  const event: unknown = input;
  if (!isDict(event)) return input;
  scrubRequest(event.request);
  if (typeof event.transaction === 'string') event.transaction = stripUrlQuery(event.transaction);
  if (typeof event.message === 'string') event.message = scrubUrlsInText(event.message);
  if (isDict(event.logentry) && typeof event.logentry.message === 'string') {
    event.logentry.message = scrubUrlsInText(event.logentry.message);
  }

  const exception = event.exception;
  if (isDict(exception) && Array.isArray(exception.values)) {
    for (const ex of exception.values) {
      if (isDict(ex) && typeof ex.value === 'string') ex.value = scrubUrlsInText(ex.value);
    }
  }

  if (Array.isArray(event.breadcrumbs)) {
    for (const b of event.breadcrumbs) scrubBreadcrumb(b);
  }

  if (Array.isArray(event.spans)) {
    for (const span of event.spans) {
      if (!isDict(span)) continue;
      if (typeof span.description === 'string') span.description = scrubUrlsInText(span.description);
      scrubData(span.data);
    }
  }

  if (isDict(event.contexts) && isDict(event.contexts.trace)) {
    const trace = event.contexts.trace;
    if (typeof trace.description === 'string') trace.description = scrubUrlsInText(trace.description);
    scrubData(trace.data);
  }

  if (isDict(event.tags) && typeof event.tags.url === 'string') {
    event.tags.url = stripUrlQuery(event.tags.url);
  }
  return input;
}
